import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { ProblemDetails, AuthTokens } from '@/types'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token!)
    }
  })
  failedQueue = []
}

function getAccessToken(): string | null {
  return localStorage.getItem('access_token')
}

function getRefreshToken(): string | null {
  return localStorage.getItem('refresh_token')
}

function setTokens(tokens: AuthTokens): void {
  localStorage.setItem('access_token', tokens.access_token)
  localStorage.setItem('refresh_token', tokens.refresh_token)
}

function clearTokens(): void {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}

let activeAccountUuid: string | null = localStorage.getItem('active_account_uuid')

export function getActiveAccountUuid(): string | null {
  return activeAccountUuid
}

export function setActiveAccountUuid(uuid: string | null): void {
  activeAccountUuid = uuid
  if (uuid) {
    localStorage.setItem('active_account_uuid', uuid)
  } else {
    localStorage.removeItem('active_account_uuid')
  }
}

/** Create the Axios instance with base configuration */
const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30000,
})

/** Request interceptor: attach bearer token */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    if (activeAccountUuid) {
      config.headers['X-Account-UUID'] = activeAccountUuid
    }
    return config
  },
  (error) => Promise.reject(error)
)

function redirectToLogin(): void {
  const path = window.location.pathname + window.location.search
  if (path.startsWith('/app') || path.startsWith('/login')) {
    const redirect = path.startsWith('/login') ? '' : `?redirect=${encodeURIComponent(path)}`
    window.location.assign(`/login${redirect}`)
  }
}

/** Response interceptor: handle 401 with token refresh */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ProblemDetails>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // If 401 and we haven't already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry auth endpoints (login, register, refresh)
      if (
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/register') ||
        originalRequest.url?.includes('/auth/2fa/verify') ||
        originalRequest.url?.includes('/auth/step-up')
      ) {
        if (originalRequest.url?.includes('/auth/refresh')) {
          clearTokens()
          redirectToLogin()
        }
        return Promise.reject(error)
      }

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return apiClient(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = getRefreshToken()
      if (!refreshToken) {
        clearTokens()
        redirectToLogin()
        return Promise.reject(error)
      }

      try {
        const { data } = await axios.post<AuthTokens>(`${BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        })

        setTokens(data)
        processQueue(null, data.access_token)

        originalRequest.headers.Authorization = `Bearer ${data.access_token}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        clearTokens()
        redirectToLogin()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export { apiClient, getAccessToken, getRefreshToken, setTokens, clearTokens }
export default apiClient
